import React, { useState, useEffect } from 'react';
import { useLang } from '../../i18n/context';
import { api } from '../../services/api';
import type { Source, TabId } from '../../types';

interface SupportTabProps {
  onTabChange: (tab: TabId) => void;
}

export function SupportTab({ onTabChange }: SupportTabProps) {
  const { t } = useLang();
  const [sources, setSources] = useState<Source[]>([]);
  // Assembled at runtime (not a single literal) so the bundle/static HTML can't be
  // grepped for the address by naive harvesters. Clickable mailto; Proton's own
  // spam filtering covers the rest.
  const contactEmail = ['riddimradar', 'proton.me'].join('@');

  useEffect(() => {
    api.getSources().then(data => {
      // Only show validated sources (active + not in probation)
      // Show all validated active sources: active, OK status, not radar
      // Auto-discovered sources appear here too once promoted to OK (they earned it)
      const validated = data.filter((s: any) =>
        s.is_active
        && (!s.source_status || s.source_status === 'ok')
        && !s.radar_id
      );
      setSources(validated);
    }).catch(() => {});
  }, []);

  const typeColors: Record<string, string> = {
    instagram: '#e1306c',
    facebook: '#1877f2',
    web: '#4caf50',
  };

  const typeIcons: Record<string, string> = {
    instagram: 'IG',
    facebook: 'FB',
    web: 'WEB',
  };

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(26,26,46,0.95) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(201,168,76,0.2)',
      }}>
        {/* Dub console knob decoration */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: i === 2 ? '#c9a84c' : 'rgba(201,168,76,0.3)',
              boxShadow: i === 2 ? '0 0 8px rgba(201,168,76,0.5)' : 'none',
            }} />
          ))}
        </div>
        <h1 style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '22px',
          fontWeight: 700,
          color: '#c9a84c',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          margin: '0 0 4px 0',
        }}>
          {t('support.title')}
        </h1>
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '12px',
        }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: i === 2 ? '#c9a84c' : 'rgba(201,168,76,0.3)',
              boxShadow: i === 2 ? '0 0 8px rgba(201,168,76,0.5)' : 'none',
            }} />
          ))}
        </div>
      </div>

      {/* Message */}
      <div style={{
        fontSize: '14px',
        lineHeight: '1.8',
        color: 'var(--text-color)',
      }}>
        <p style={{ marginBottom: '16px' }}>
          {t('support.intro')}
        </p>
        <p style={{ marginBottom: '16px' }}>
          {t('support.sources_message')}
        </p>
        <p style={{ marginBottom: '16px' }}>
          {t('support.action_intro')}{' '}
          <button
            onClick={() => onTabChange('artists')}
            style={{
              background: 'none', border: 'none', color: '#c9a84c',
              cursor: 'pointer', textDecoration: 'underline', fontSize: '14px',
              fontFamily: 'inherit', padding: 0,
            }}
          >
            {t('support.artists_link')}
          </button>
        </p>
        <p style={{ marginBottom: '24px' }}>
          {t('support.action_flyer')}
        </p>
        <p style={{
          marginBottom: '24px', padding: '16px',
          background: 'rgba(201,168,76,0.06)',
          borderLeft: '3px solid rgba(201,168,76,0.3)',
          borderRadius: '0 6px 6px 0',
          fontStyle: 'italic',
          color: 'var(--text-muted)',
        }}>
          {t('support.future')}
        </p>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
            {t('support.thanks')}
          </p>
          <p style={{
            fontSize: '20px', fontWeight: 700, color: '#c9a84c',
            fontFamily: 'JetBrains Mono, monospace', letterSpacing: '2px',
          }}>
            {t('support.bless')}
          </p>
        </div>

        {/* Contact */}
        <div style={{
          textAlign: 'center', marginBottom: '8px', fontSize: '13px',
          color: 'var(--text-muted)', lineHeight: '1.7',
        }}>
          {t('support.contact')}{' '}
          <a
            href={`mailto:${contactEmail}`}
            style={{ color: '#c9a84c', textDecoration: 'none', fontWeight: 600 }}
          >
            {contactEmail}
          </a>
        </div>
      </div>

      {/* Divider */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        margin: '24px 0',
      }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
        <span style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '11px', fontWeight: 700,
          color: '#c9a84c', letterSpacing: '2px',
          textTransform: 'uppercase',
        }}>
          {t('support.our_sources')}
        </span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(201,168,76,0.2)' }} />
      </div>

      {/* Source list */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '10px',
      }}>
        {sources.map(s => {
          const type = s.type || 'web';
          const color = typeColors[type] || '#888';
          const icon = typeIcons[type] || '?';
          const displayName = s.name
            .replace(' (Instagram)', '')
            .replace(' (Facebook)', '')
            .replace(' [auto]', '');

          return (
            <a
              key={s.id}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = color;
                (e.currentTarget as HTMLElement).style.background = `${color}11`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)';
              }}
            >
              <span style={{
                fontSize: '9px', fontWeight: 700, padding: '2px 5px',
                borderRadius: '3px', background: `${color}22`,
                color: color, letterSpacing: '0.5px',
                flexShrink: 0,
              }}>
                {icon}
              </span>
              <span style={{
                fontSize: '13px', fontWeight: 600,
                color: 'var(--text-color)',
                overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayName}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
